import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import type { ApiResponse } from '../types/task';

interface ActiveYearResponse {
  year: number;
  conferenceDate?: string;
}

interface ActiveYearContextValue {
  /**
   * Effective year for the current viewer — equals globalYear unless the
   * user has set a personal override. `null` while still loading.
   */
  year: number | null;
  /** Global active year (server-truth). Drives the cutover & defaults. */
  globalYear: number | null;
  /** ISO date (YYYY-MM-DD) of the conference, if set globally. */
  conferenceDate: string | null;
  isLoading: boolean;
  /** True iff the viewer is looking at a year other than the global one. */
  isOverriding: boolean;
  /** Set a personal year override (any user). */
  setOverride: (year: number) => void;
  /** Clear the personal override and return to the global year. */
  clearOverride: () => void;
  /** Admins only. Updates the GLOBAL active year for everyone. */
  setYear: (input: { year: number; conferenceDate?: string }) => Promise<ActiveYearResponse>;
  isMutating: boolean;
}

const ActiveYearContext = createContext<ActiveYearContextValue | undefined>(undefined);

const QUERY_KEY = ['system', 'active-year'];
const OVERRIDE_STORAGE_KEY = 'bsides-year-override';

function readStoredOverride(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(OVERRIDE_STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStoredOverride(year: number | null): void {
  try {
    if (year === null) {
      window.localStorage.removeItem(OVERRIDE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(OVERRIDE_STORAGE_KEY, String(year));
    }
  } catch {
    // ignore quota / private-mode failures
  }
}

export function ActiveYearProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<ApiResponse<ActiveYearResponse>>('/system/active-year');
      return res.data!;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Personal override: URL `?year=` wins over localStorage. Updating either
  // keeps the two in sync so deep links + reloads behave the same way.
  const urlYear = (() => {
    const raw = searchParams.get('year');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  const [storedOverride, setStoredOverride] = useState<number | null>(readStoredOverride);
  const override = urlYear ?? storedOverride;

  // Keep localStorage in sync whenever the URL drops an explicit year.
  useEffect(() => {
    if (urlYear !== null && urlYear !== storedOverride) {
      setStoredOverride(urlYear);
      writeStoredOverride(urlYear);
    }
  }, [urlYear, storedOverride]);

  const setOverride = useCallback(
    (yearValue: number) => {
      setStoredOverride(yearValue);
      writeStoredOverride(yearValue);
      const next = new URLSearchParams(searchParams);
      next.set('year', String(yearValue));
      setSearchParams(next, { replace: true });
      // Year-scoped data must refetch immediately so the UI changes visibly.
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['milestones'] });
      queryClient.invalidateQueries({ queryKey: ['navigation'] });
      queryClient.invalidateQueries({ queryKey: ['home'] });
    },
    [searchParams, setSearchParams, queryClient],
  );

  const clearOverride = useCallback(() => {
    setStoredOverride(null);
    writeStoredOverride(null);
    const next = new URLSearchParams(searchParams);
    next.delete('year');
    setSearchParams(next, { replace: true });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    queryClient.invalidateQueries({ queryKey: ['milestones'] });
    queryClient.invalidateQueries({ queryKey: ['navigation'] });
    queryClient.invalidateQueries({ queryKey: ['home'] });
  }, [searchParams, setSearchParams, queryClient]);

  const mutation = useMutation({
    mutationFn: async (input: { year: number; conferenceDate?: string }) => {
      const res = await api.put<ApiResponse<ActiveYearResponse>>('/system/active-year', input);
      return res.data!;
    },
    onSuccess: async (resp) => {
      queryClient.setQueryData(QUERY_KEY, resp);
      // Force a refetch (not just invalidate) so observers actually see fresh
      // data — the previous behavior was the "year doesn't update tasks list"
      // bug because invalidate alone left stale data on screen.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['tasks'] }),
        queryClient.refetchQueries({ queryKey: ['milestones'] }),
        queryClient.refetchQueries({ queryKey: ['navigation'] }),
        queryClient.refetchQueries({ queryKey: ['home'] }),
      ]);
    },
  });

  const globalYear = data?.year ?? null;
  const effectiveYear = override ?? globalYear;
  const isOverriding =
    override !== null && globalYear !== null && override !== globalYear;

  const value = useMemo<ActiveYearContextValue>(
    () => ({
      year: effectiveYear,
      globalYear,
      conferenceDate: data?.conferenceDate ?? null,
      isLoading,
      isOverriding,
      setOverride,
      clearOverride,
      setYear: mutation.mutateAsync,
      isMutating: mutation.isPending,
    }),
    [
      effectiveYear,
      globalYear,
      data?.conferenceDate,
      isLoading,
      isOverriding,
      setOverride,
      clearOverride,
      mutation.mutateAsync,
      mutation.isPending,
    ],
  );

  return <ActiveYearContext.Provider value={value}>{children}</ActiveYearContext.Provider>;
}

export function useActiveYear() {
  const ctx = useContext(ActiveYearContext);
  if (!ctx) throw new Error('useActiveYear must be used within ActiveYearProvider');
  return ctx;
}
